import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  IconButton,
  Link,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { userManagementAPI } from "@platformx/authoring-apis";
import { useDebounce } from "@platformx/utilities";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { STATUS_FILTER_OPTIONS } from "./helperContentList";

/** Wire format the authoring gateway `dateFilter` expects. */
const API_DATE_FORMAT = "YYYY-MM-DD";
/** Display format shown in the design (e.g. 09-10-2025). */
const DISPLAY_DATE_FORMAT = "DD-MM-YYYY";

export type FilterValues = {
  /** "YYYY-MM-DD" or "" */
  fromDate: string;
  /** "YYYY-MM-DD" or "" */
  toDate: string;
  status: string[];
  author: string[];
};

type AuthorOption = { label: string; value: string };

type DateFilterModalProps = {
  open: boolean;
  onClose: () => void;
  /** Currently applied values (used to seed the dialog when it opens). */
  value: FilterValues;
  // eslint-disable-next-line no-unused-vars
  onApply: (next: FilterValues) => void;
};

const toDayjs = (value: string): Dayjs | null => (value ? dayjs(value, API_DATE_FORMAT) : null);

const toApiString = (value: Dayjs | null): string =>
  value && value.isValid() ? value.format(API_DATE_FORMAT) : "";

type PresetKey = "today" | "week" | "month";

/** Range each preset resolves to, computed fresh each render off "now". */
const getPresetRange = (key: PresetKey): { start: Dayjs; end: Dayjs } => {
  const now = dayjs();
  switch (key) {
    case "today":
      return { start: now.startOf("day"), end: now.endOf("day") };
    case "week":
      return { start: now.startOf("week"), end: now.endOf("week") };
    case "month":
    default:
      return { start: now.startOf("month"), end: now.endOf("month") };
  }
};

/**
 * Derive which preset (if any) matches the current range, rather than storing a
 * "selected preset" flag — so a manual date edit or Reset automatically clears
 * the highlight because the values no longer match a preset's range.
 */
const matchedPreset = (from: Dayjs | null, to: Dayjs | null): PresetKey | null => {
  if (!from || !to || !from.isValid() || !to.isValid()) return null;
  const keys: PresetKey[] = ["today", "week", "month"];
  return (
    keys.find((key) => {
      const { start, end } = getPresetRange(key);
      return from.isSame(start, "day") && to.isSame(end, "day");
    }) || null
  );
};

const DateFilterModal = ({ open, onClose, value, onApply }: DateFilterModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [from, setFrom] = useState<Dayjs | null>(null);
  const [to, setTo] = useState<Dayjs | null>(null);
  const [status, setStatus] = useState<string[]>([]);
  const [author, setAuthor] = useState<string[]>([]);
  const [authorSearchInput, setAuthorSearchInput] = useState("");
  const [authorOptions, setAuthorOptions] = useState<AuthorOption[]>([]);
  const [authorLoading, setAuthorLoading] = useState(false);
  const debouncedAuthorSearch = useDebounce(authorSearchInput, 500);

  // Seed local state from the applied value every time the dialog is opened,
  // so cancelling (closing without Apply) discards in-progress edits.
  useEffect(() => {
    if (open) {
      setFrom(toDayjs(value.fromDate));
      setTo(toDayjs(value.toDate));
      setStatus(value.status || []);
      setAuthor(value.author || []);
      setAuthorSearchInput("");
    }
  }, [open, value.fromDate, value.toDate, value.status?.join(","), value.author?.join(",")]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAuthorLoading(true);
    userManagementAPI
      .fetchUsers({
        start: 0,
        rows: 20,
        isAuthoringUser: true,
        isRenderingUser: false,
        isCommunityUser: false,
        search: debouncedAuthorSearch,
        user_onboarding_status: false,
      })
      .then((resp: any) => {
        if (cancelled) return;
        const users = resp?.authoring_userList || [];
        const seen = new Set<string>();
        const options: AuthorOption[] = [];
        users.forEach((u: any) => {
          const name = `${u?.first_name || ""} ${u?.last_name || ""}`.trim();
          if (!name || seen.has(name)) return;
          seen.add(name);
          options.push({ label: name, value: name });
        });
        setAuthorOptions(options);
      })
      .catch(() => {
        if (!cancelled) setAuthorOptions([]);
      })
      .finally(() => {
        if (!cancelled) setAuthorLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedAuthorSearch]);

  // A typed-but-incomplete/garbage date is a non-null invalid Dayjs; block Apply
  // on it so a bad value can't be silently dropped.
  const hasInvalidInput = Boolean((from && !from.isValid()) || (to && !to.isValid()));
  const isInvalidRange =
    !hasInvalidInput &&
    Boolean(from && to && from.isValid() && to.isValid() && from.isAfter(to, "day"));
  const activeCount =
    (from || to ? 1 : 0) + (status.length > 0 ? 1 : 0) + (author.length > 0 ? 1 : 0);
  const selectedPreset = matchedPreset(from, to);

  const applyPreset = (key: PresetKey) => {
    const { start, end } = getPresetRange(key);
    setFrom(start);
    setTo(end);
  };

  const handleResetDateRange = () => {
    setFrom(null);
    setTo(null);
  };

  const handleStatusChange = (event: any) => {
    const {
      target: { value: selected },
    } = event;
    setStatus(typeof selected === "string" ? selected.split(",") : selected);
  };

  const handleAuthorChange = (event: any) => {
    const {
      target: { value: selected },
    } = event;
    setAuthor(typeof selected === "string" ? selected.split(",") : selected);
  };

  const handleResetAll = () => {
    setFrom(null);
    setTo(null);
    setStatus([]);
    setAuthor([]);
    setAuthorSearchInput("");
    onApply({ fromDate: "", toDate: "", status: [], author: [] });
    onClose();
  };

  const handleApply = () => {
    if (isInvalidRange) return;
    onApply({ fromDate: toApiString(from), toDate: toApiString(to), status, author });
    onClose();
  };

  const presetButtonSx = (key: PresetKey) => {
    const isSelected = selectedPreset === key;
    return {
      flex: 1,
      textTransform: "none",
      borderColor: isSelected ? "#2d2d39" : "#D9DBE9",
      backgroundColor: isSelected ? "#2d2d39" : "transparent",
      color: isSelected ? "#ffffff" : "#14142B",
      "&:hover": {
        borderColor: "#2d2d39",
        backgroundColor: isSelected ? "#2d2d39" : "transparent",
      },
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={false}
      aria-labelledby='date-filter-dialog-title'
      sx={{
        ".Platform-x-Dialog-paper": {
          borderRadius: "12px",
          padding: "20px",
          margin: { xs: "16px" },
          width: { xs: "100%", sm: "480px" },
          maxWidth: { xs: "calc(100% - 32px)", sm: "480px" },
        },
      }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #EFF0F6",
          paddingBottom: "12px",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FilterAltOutlinedIcon sx={{ fontSize: "20px", color: "#14142B" }} />
          <Typography variant='h5bold' id='date-filter-dialog-title'>
            {t("filter_by")}
          </Typography>
        </Box>
        <IconButton
          aria-label='close'
          data-testid='date-filter-close'
          onClick={onClose}
          size='small'
          sx={{ color: "#8991A5" }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      {/* Date Range section */}
      <Box sx={{ paddingTop: "16px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}>
          <Typography variant='h6bold'>{t("date_range")}</Typography>
          <Link
            component='button'
            type='button'
            underline='none'
            data-testid='date-filter-reset'
            onClick={handleResetDateRange}
            sx={{ color: "#1E64ED", fontWeight: 600, cursor: "pointer" }}>
            {t("reset")}
          </Link>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "flex", gap: "16px", flexDirection: { xs: "column", sm: "row" } }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant='p4regular' sx={{ color: "#57596C", marginBottom: "4px" }}>
                {t("from")}
              </Typography>
              <DatePicker
                value={from}
                onChange={(newValue) => setFrom(newValue as Dayjs | null)}
                inputFormat={DISPLAY_DATE_FORMAT}
                mask='__-__-____'
                maxDate={to || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(from && !from.isValid())}
                    data-testid='date-filter-from'
                    inputProps={{ ...params.inputProps, placeholder: DISPLAY_DATE_FORMAT }}
                  />
                )}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='p4regular' sx={{ color: "#57596C", marginBottom: "4px" }}>
                {t("to")}
              </Typography>
              <DatePicker
                value={to}
                onChange={(newValue) => setTo(newValue as Dayjs | null)}
                inputFormat={DISPLAY_DATE_FORMAT}
                mask='__-__-____'
                minDate={from || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(to && !to.isValid())}
                    data-testid='date-filter-to'
                    inputProps={{ ...params.inputProps, placeholder: DISPLAY_DATE_FORMAT }}
                  />
                )}
              />
            </Box>
          </Box>
        </LocalizationProvider>

        {(isInvalidRange || hasInvalidInput) && (
          <Typography variant='p4regular' sx={{ color: theme.palette.customColor.ERROR, mt: 1 }}>
            {hasInvalidInput ? t("date_invalid") : t("date_range_invalid")}
          </Typography>
        )}

        {/* Presets */}
        <Box sx={{ display: "flex", gap: "12px", marginTop: "16px" }}>
          <Button
            variant='outlined'
            data-testid='date-filter-preset-today'
            aria-pressed={selectedPreset === "today"}
            onClick={() => applyPreset("today")}
            sx={presetButtonSx("today")}>
            {t("today")}
          </Button>
          <Button
            variant='outlined'
            data-testid='date-filter-preset-week'
            aria-pressed={selectedPreset === "week"}
            onClick={() => applyPreset("week")}
            sx={presetButtonSx("week")}>
            {t("this_week")}
          </Button>
          <Button
            variant='outlined'
            data-testid='date-filter-preset-month'
            aria-pressed={selectedPreset === "month"}
            onClick={() => applyPreset("month")}
            sx={presetButtonSx("month")}>
            {t("this_month")}
          </Button>
        </Box>
      </Box>

      {/* Status section */}
      <Box sx={{ paddingTop: "20px" }}>
        <Typography variant='h6bold' sx={{ marginBottom: "10px" }}>
          {t("status")}
        </Typography>
        <FormControl fullWidth>
          <Select
            size='medium'
            multiple
            displayEmpty
            data-testid='date-filter-status'
            value={status}
            onChange={handleStatusChange}
            IconComponent={ExpandMoreIcon}
            MenuProps={{
              PaperProps: { sx: { maxHeight: "260px", overflowY: "auto" } },
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
            renderValue={(selected: unknown) => {
              const selectedValues = selected as string[];
              if (!selectedValues.length) {
                return <Typography sx={{ color: "#8991A5" }}>{t("select")}</Typography>;
              }
              return selectedValues
                .map((val) =>
                  t(STATUS_FILTER_OPTIONS.find((opt) => opt.value === val)?.labelKey || val),
                )
                .join(", ");
            }}>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={status.indexOf(option.value) > -1} />
                <Typography variant='p2regular'>{t(option.labelKey)}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Author section */}
      <Box sx={{ paddingTop: "20px" }}>
        <Typography variant='h6bold' sx={{ marginBottom: "10px" }}>
          {t("author")}
        </Typography>
        <FormControl fullWidth>
          <Select
            size='medium'
            multiple
            displayEmpty
            data-testid='date-filter-author'
            value={author}
            onChange={handleAuthorChange}
            IconComponent={ExpandMoreIcon}
            MenuProps={{
              autoFocus: false,
              PaperProps: { sx: { maxHeight: "300px", overflowY: "auto" } },
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
            onClose={() => setAuthorSearchInput("")}
            renderValue={(selected: unknown) => {
              const selectedValues = selected as string[];
              if (!selectedValues.length) {
                return <Typography sx={{ color: "#8991A5" }}>{t("select")}</Typography>;
              }
              return selectedValues.join(", ");
            }}>
            <ListSubheader sx={{ padding: "8px 12px" }}>
              <Typography variant='p4regular' sx={{ color: "#57596C", marginBottom: "4px" }}>
                {t("filter_author")}
              </Typography>
              <TextField
                size='small'
                fullWidth
                autoFocus
                placeholder={t("author")}
                value={authorSearchInput}
                data-testid='date-filter-author-search'
                onChange={(e) => setAuthorSearchInput(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </ListSubheader>
            {authorLoading && (
              <MenuItem disabled>
                <Typography variant='p2regular'>{t("loading")}</Typography>
              </MenuItem>
            )}
            {!authorLoading && authorOptions.length === 0 && (
              <MenuItem disabled>
                <Typography variant='p2regular'>{t("no_results")}</Typography>
              </MenuItem>
            )}
            {!authorLoading &&
              authorOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={author.indexOf(option.value) > -1} />
                  <Typography variant='p2regular'>{option.label}</Typography>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          marginTop: "24px",
          borderTop: "1px solid #EFF0F6",
          paddingTop: "16px",
          flexDirection: fullScreen ? "column" : "row",
        }}>
        <Button
          variant='secondaryButton'
          fullWidth
          data-testid='date-filter-reset-all'
          onClick={handleResetAll}>
          {t("reset_all")}
        </Button>
        <Button
          variant='primaryButton'
          fullWidth
          disabled={isInvalidRange || hasInvalidInput}
          data-testid='date-filter-apply'
          onClick={handleApply}>
          {activeCount > 0 ? `${t("apply_filter")} (${activeCount})` : t("apply_filter")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default DateFilterModal;
